<script lang="ts">
    import { onMount } from 'svelte';
    import { link } from 'svelte-spa-router';
    import { push } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';
    import { AppEmptyState, AppLoadingState, AppPageHeader } from '../../framework/components/app';
    import '../admin/admin-list.css';

    interface BriefType {
        uuid: string;
        name: string;
    }

    let briefTypes: BriefType[] = [];
    let isLoading = false;

    onMount(async () => {
        await loadBriefTypes();
    });

    async function loadBriefTypes(): Promise<void> {
        isLoading = true;
        spinner.show();
        try {
            const response = (await api.crudIndex('xplorit-type-brief', {
                column: 'name',
                order: 'asc',
                per_page: 500,
            })) as { success?: boolean; data?: BriefType[] | { data?: BriefType[] } };

            if (response?.success && response.data) {
                briefTypes = Array.isArray(response.data) ? response.data : response.data.data || [];
            } else {
                briefTypes = [];
            }
        } catch (error) {
            console.error(error);
            toast.showNotification(false, 'Failed to load brief types');
            briefTypes = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function openBriefType(uuid: string): void {
        push(`/admin/knowledge/brief-types/${uuid}`);
    }

    function handleKeydown(event: KeyboardEvent, uuid: string): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openBriefType(uuid);
        }
    }
</script>

<div class="page-shell admin-list">
    <AppPageHeader
        title="Brief-type knowledge"
        subtitle="Rubrics, exemplars, and scoring guidance per brief type."
        showAction={false}
    >
        <svelte:fragment slot="actions">
            <a use:link href="/admin/knowledge/platform" class="secondary-link">Platform knowledge</a>
            <a use:link href="/admin/knowledge/test" class="secondary-link">Test retrieval</a>
        </svelte:fragment>
    </AppPageHeader>

    {#if isLoading}
        <AppLoadingState label="Loading brief types…" />
    {:else if briefTypes.length === 0}
        <AppEmptyState icon="file text" title="No brief types" description="Create brief types before adding per-type knowledge." />
    {:else}
        <div class="card-grid">
            {#each briefTypes as bt (bt.uuid)}
                <button
                    type="button"
                    class="type-card"
                    on:click={() => openBriefType(bt.uuid)}
                    on:keydown={(e) => handleKeydown(e, bt.uuid)}
                >
                    <i class="file alternate icon" aria-hidden="true"></i>
                    <span class="type-name">{bt.name}</span>
                    <i class="chevron right icon" aria-hidden="true"></i>
                </button>
            {/each}
        </div>
    {/if}
</div>

<style>
    .secondary-link {
        color: var(--primary);
        text-decoration: none;
        font-weight: 500;
        margin-right: var(--space-4);
    }

    .secondary-link:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .card-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: var(--space-4);
    }

    .type-card {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        width: 100%;
        text-align: left;
        padding: var(--space-4) var(--space-5);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        background: var(--white);
        box-shadow: var(--shadow-card);
        cursor: pointer;
        color: var(--grey);
        font: inherit;
    }

    .type-card:hover {
        border-color: var(--primary);
    }

    .type-card:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .type-name {
        flex: 1;
        font-weight: 500;
    }
</style>
